import { forwardRef, ReactElement, Ref, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GroupBase,
  PropsValue,
  OnChangeValue,
  ActionMeta,
  AsyncPaginateSelectProps,
  SelectComponentsConfig,
  SelectForwardRef,
  useAsyncPaginateMultiSelect,
} from '@any-ui-react/select';
import useAsyncEffect from 'use-async-effect';

import {
  LoadOptionDefaultOption,
  LoadOptionAdditional,
  MdAccountFilterInput,
} from '@anyx/common-graphql';
import { LazySelectV2 } from '@anyx/shared-ui';
import { BasicOption, EMPTY_DEFAULT } from '@anyx/shared-utils';

import { createSelectAllMenuList, SelectAllMenuListState } from './AccountSelectMenuList';
import {
  SELECT_ALL_LIMIT,
  useAccountSelect,
  useAccountsSelect,
  useAccountsSelectAll,
} from './operations';

interface AccountSelectProps<T = string, IsMulti extends boolean = boolean> extends Omit<
  AsyncPaginateSelectProps<
    LoadOptionDefaultOption<T>,
    GroupBase<LoadOptionDefaultOption<T>>,
    LoadOptionAdditional,
    IsMulti
  >,
  'loadOptions' | 'components' | 'value'
> {
  filters?: MdAccountFilterInput;
  isMulti?: IsMulti;
  /**
   * Adds a "select all" action to the bottom of the menu, applying to whatever
   * `filters` currently narrow the list. Multi-select only.
   */
  allowSelectAll?: boolean;
  value?: PropsValue<LoadOptionDefaultOption<T> | T>;
  onChange?: (
    newValue: OnChangeValue<LoadOptionDefaultOption<T>, IsMulti>,
    actionMeta: ActionMeta<LoadOptionDefaultOption<T> | T>
  ) => void;
}
const AccountSelectGn = <T = string, IsMulti extends boolean = false>(
  { value: accountIds, filters, isMulti, allowSelectAll, onChange, ...rest }: AccountSelectProps<
    T,
    IsMulti
  >,
  ref: SelectForwardRef<LoadOptionDefaultOption<T>, IsMulti, GroupBase<LoadOptionDefaultOption<T>>>
) => {
  const arrayValue = useMemo(() => {
    const value = accountIds ? accountIds : [];
    return Array.isArray(value) ? value : [value];
  }, [accountIds]);

  const [values, setValues] = useState<BasicOption<T>[]>([]);

  const { t } = useTranslation('shared');
  const paginateMultiSelectProps = useAsyncPaginateMultiSelect<
    LoadOptionDefaultOption<T>,
    GroupBase<LoadOptionDefaultOption<T>>
  >({
    getSelectedLabel: (selectedCount) =>
      t('shared.select.accountSelected', { ns: 'shared', amount: selectedCount }),
  });

  const { getAccountDetails } = useAccountSelect();
  const { loadOptions } = useAccountsSelect<T>({ filters });

  const selectAllEnabled = Boolean(isMulti && allowSelectAll);
  const { total, loadAllOptions, loadingAll } = useAccountsSelectAll<T>({
    filters,
    skip: !selectAllEnabled,
  });

  const handleSelectAll = useCallback(async () => {
    const allOptions = await loadAllOptions();

    setValues(allOptions);
    // Only reachable with isMulti set, where OnChangeValue is the option array.
    onChange?.(allOptions as unknown as OnChangeValue<LoadOptionDefaultOption<T>, IsMulti>, {
      action: 'select-option',
      option: undefined,
    });
  }, [loadAllOptions, onChange]);

  const selectAllLabel = useMemo(() => {
    if (loadingAll) {
      return t('shared.button.loading', { ns: 'shared' });
    }

    if (total > SELECT_ALL_LIMIT) {
      return t('shared.select.selectAllLimited', { ns: 'shared', amount: SELECT_ALL_LIMIT });
    }

    return t('shared.select.selectAllCount', { ns: 'shared', amount: total });
  }, [loadingAll, t, total]);

  // Read at MenuList render time, so the component below can stay identity-stable.
  const selectAllStateRef = useRef<SelectAllMenuListState>({
    label: selectAllLabel,
    disabled: true,
    onSelectAll: () => undefined,
  });
  selectAllStateRef.current = {
    label: selectAllLabel,
    disabled: loadingAll || total > SELECT_ALL_LIMIT,
    onSelectAll: () => void handleSelectAll(),
  };

  const SelectAllMenuList = useMemo(
    () =>
      createSelectAllMenuList<
        LoadOptionDefaultOption<T>,
        IsMulti,
        GroupBase<LoadOptionDefaultOption<T>>
      >(selectAllStateRef),
    []
  );

  useAsyncEffect(
    async (isMounted) => {
      if (!arrayValue || arrayValue.length === 0) {
        setValues([]);
        return;
      }

      //TODO Currently we will show the name for one value, and X selected when more,
      //That s why we only care to load the first value name and ignore the other names
      try {
        if (!isMounted()) return;
        const accountDetails = await getAccountDetails({ accountId: arrayValue[0] });
        const displayValue = accountDetails?.name || EMPTY_DEFAULT;
        const selectedOption = arrayValue?.map((value, i) => ({
          value: value,
          label: i === 0 ? displayValue : EMPTY_DEFAULT,
        }));
        setValues(selectedOption);
      } catch (e) {
        if (!isMounted()) return;
      }
    },
    [arrayValue]
  );

  const multiProps = isMulti ? paginateMultiSelectProps : {};

  // Spread conditionally rather than always passing `components`: when the footer is not in
  // play, multiProps' own components (Option, MultiValue, Group wrappers) have to reach the
  // select untouched, and an explicit `components={undefined}` would wipe them out.
  //
  // The annotation is load-bearing. useAsyncPaginateMultiSelect types its components with
  // IsMulti pinned to true, and letting that flow in unannotated pins the whole element to
  // true, which then rejects this component's own IsMulti-typed `onChange` and `ref`.
  const selectAllProps: {
    components?: SelectComponentsConfig<
      LoadOptionDefaultOption<T>,
      IsMulti,
      GroupBase<LoadOptionDefaultOption<T>>
    >;
  } =
    selectAllEnabled && total > 0
      ? {
          components: {
            ...paginateMultiSelectProps.components,
            MenuList: SelectAllMenuList,
          } as SelectComponentsConfig<
            LoadOptionDefaultOption<T>,
            IsMulti,
            GroupBase<LoadOptionDefaultOption<T>>
          >,
        }
      : {};

  return (
    <LazySelectV2
      {...multiProps}
      className="select-has-min-w-menu [&_.any-select\_\_value-container]:flex-nowrap [&_.option-label]:truncate [&_label]:truncate"
      isClearable
      isSearchable
      // TODO this line should not be needed
      // It is currently used to redraw the component so it can trigger the query again
      // This whole MdStore/Supplier/Account selector need to be cleaned up
      key={`${JSON.stringify(filters)}`}
      {...rest}
      {...selectAllProps}
      value={values}
      onChange={(value, meta) => {
        onChange?.(value, meta);
        if (value === null) {
          setValues([]);
        } else {
          setValues(Array.isArray(value) ? value : [value]);
        }
      }}
      ref={ref}
      loadOptions={loadOptions}
    />
  );
};

export const AccountSelect = forwardRef(AccountSelectGn) as <
  T = string,
  IsMulti extends boolean = boolean,
>(
  p: AccountSelectProps<T, IsMulti> & {
    ref?: Ref<
      SelectForwardRef<LoadOptionDefaultOption<T>, IsMulti, GroupBase<LoadOptionDefaultOption<T>>>
    >;
  }
) => ReactElement;
